import { useMutation, useQuery } from "react-query";
import { format, parse } from "date-fns";
import { useEffect, useState } from "react";
import queryString from "query-string";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import Select from "react-select";
import { Formik, useFormik } from "formik";
import * as Yup from "yup";

import { ExchangeFunds, EditIcon, DeleteIcon, ViewIcon } from "../Icons";
import "./../../assets/scss/reports/cashbook.scss";
import useDebounce, {
  useQueryParams,
  useScrollTop,
  useUnits,
} from "../../utils/hooks";
import { useAuth } from "../../hooks/useAuth";
import ModalLoader from "../utils/ModalLoader";
import NoTableItem from "../utils/NoTableItem";
import RsDateRangePicker from "../utils/RsDateRangePicker";
import {
  fetchActionsUtil,
  paginationOptions,
  scrollToTop,
} from "../../utils/helpers";
import { toast } from "react-toastify";
import ConfirmDialog from "../ConfirmDialogue";
import ReactPaginate from "react-paginate";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { IsPrivileged } from "../DisplayChildElement";
import CreatableSelect from "react-select/creatable";
import {
  Modal,
  Form,
  Button,
  Row,
  InputGroup,
  Col,
  Spinner,
  Dropdown,
  Table,
} from "react-bootstrap";
import CachedIcon from "mdi-react/CachedIcon";
import { useStoreActions } from "easy-peasy";
import { isEmpty } from "lodash";

// Empty State Component
function EmptyState({ hasFilters, onCreateNew, onClearFilters }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center py-5 my-5">
      <div className="text-center mb-4">
        <div
          className="d-flex align-items-center justify-content-center mb-3"
          style={{
            height: "80px",
          }}
        >
          <NoTableItem size={40} color="#6c757d" />
        </div>
      </div>

      <div className="d-flex gap-2">
        <Button
          variant="primary"
          onClick={onCreateNew}
          className="d-flex align-items-center gap-2"
        >
          <ExchangeFunds size={16} color="white" />
          Create New UOM
        </Button>

        {hasFilters && (
          <Button variant="outline-secondary" onClick={onClearFilters}>
            Clear Filters
          </Button>
        )}
      </div>
    </div>
  );
}

function UOMManagement() {
  useScrollTop();

  const initialFilterParams = {
    startDate: "",
    endDate: "",
    uom: "",
    baseUom: "",
  };

  const setUomConversions = useStoreActions(
    actions => actions.setUomConversions
  );

  const { backendUrl, user: authUser } = useAuth();

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedUOM, setSelectedUOM] = useState(null);

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams(query => ({
      ...query,
      ...debouncedFilterParams,
    }));
  }, [debouncedFilterParams, setQueryParams]);

  // Fetch UOM data
  const { data, refetch, isFetching } = useQuery(
    ["UOM-MANAGEMENT", queryParams],
    () =>
      fetchActionsUtil(
        `${backendUrl}/api/uom?${queryString.stringify(queryParams)}`,
        "GET",
        authUser.token
      ),
    {
      keepPreviousData: true,
      select: data => ({
        ...data,
        uom: data?.uoms || [],
      }),
      onSuccess: data => {
        // console.log("Fetched UOM data:", data);
        if (!isEmpty(data?.uoms)) setUomConversions(data?.uoms || []);
      },
    }
  );

  // Delete UOM mutation
  const deleteUOMMutation = useMutation(
    id => fetchActionsUtil(`${backendUrl}/api/uom/${id}`, "DELETE"),
    {
      onSuccess: () => {
        toast.success("UOM deleted successfully");
        refetch();
      },
      onError: ({ message }) => {
        toast.error(message || "Unable to delete UOM: Try again");
      },
    }
  );

  // UOM validation schema
  const uomValidationSchema = Yup.object().shape({
    uom: Yup.string()
      .required("UOM is required")
      .min(2, "UOM must be at least 2 characters"),
    baseUom: Yup.string()
      .required("Sub UOM is required")
      .min(2, "Sub UOM must be at least 2 characters"),
    baseQty: Yup.number()
      .required("Base Quantity is required")
      .positive("Base Quantity must be positive")
      .min(0.01, "Base Quantity must be greater than 0"),
  });

  const handleDeleteUOM = async uomItem => {
    if (
      await ConfirmDialog({
        title: `Delete UOM`,
        description: `Are you sure you want to DELETE ${uomItem.uom} - ${
          uomItem.baseUom
        }?`,
      })
    ) {
      deleteUOMMutation.mutate(uomItem.id);
    }
  };

  const filterByDateRange = date => {
    setFilterParams({
      ...filterParams,
      startDate: format(date[0], "yyyy-MM-dd"),
      endDate: format(date[1], "yyyy-MM-dd"),
    });
  };

  const clearDateRange = () => {
    setFilterParams({
      ...filterParams,
      startDate: "",
      endDate: "",
    });
  };

  const handleSearchQueryChange = e => {
    setQueryParams({
      ...queryParams,
      [e.target.name]: e.target.value,
    });
  };

  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  // Check if any filters are applied
  const hasActiveFilters = Boolean(
    filterParams.uom ||
      filterParams.baseUom ||
      filterParams.startDate ||
      filterParams.endDate
  );

  // Clear all filters
  const clearAllFilters = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      page: 1,
      limit: queryParams.limit,
      ...initialFilterParams,
    });
  };

  const hasData = data?.uom && data.uom.length > 0;

  return (
    <main className="cash-book table-sticky-header">
      <div className="content">
        <header className="container">
          <h1>
            UOM Management
            <button
              title="Refresh"
              onClick={() => refetch()}
              className="btn text-primary"
            >
              <CachedIcon />
            </button>
          </h1>

          <div className="actions mr-5">
            <div>
              <InputGroup>
                <Form.Control
                  name="uom"
                  placeholder="Search UOM..."
                  value={filterParams?.uom}
                  onChange={e => handleFilterParamsChange(e)}
                />
                <InputGroup.Text>
                  <MagnifyIcon />
                </InputGroup.Text>
              </InputGroup>
            </div>

            <div>
              <InputGroup>
                <Form.Control
                  name="baseUom"
                  placeholder="Search Sub UOM..."
                  value={filterParams?.baseUom}
                  onChange={e => handleFilterParamsChange(e)}
                />
                <InputGroup.Text>
                  <MagnifyIcon />
                </InputGroup.Text>
              </InputGroup>
            </div>

            <div className="position-relative">
              {/*  <RsDateRangePicker
                placement="bottomEnd"
                value={
                  filterParams.startDate && filterParams.endDate
                    ? [
                        parse(filterParams.startDate, "yyyy-MM-dd", new Date()),
                        parse(filterParams.endDate, "yyyy-MM-dd", new Date()),
                      ]
                    : []
                }
                onClean={() => clearDateRange()}
                onOk={date => filterByDateRange(date)}
              /> */}
            </div>

            <button
              onClick={() => setOpenCreateModal(true)}
              className="btn bg-primary text-white"
            >
              Create New <ExchangeFunds color="white" />
            </button>
          </div>
        </header>

        {/* Show table only when there's data */}
        {hasData && (
          <div>
            <Table borderless striped className="product-table container">
              <thead className="position-sticky top-0">
                <tr>
                  <th />
                  <th>S/N</th>
                  <th>Title</th>
                  <th>UOM</th>
                  <th>Sub UOM</th>
                  <th>Base Quantity</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {data?.uom?.map((item, i) => (
                  <tr key={item.id || i}>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle
                          variant=""
                          className="bg-white border-0"
                          bsPrefix="print more"
                        >
                          <DotsVerticalIcon />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          popperConfig={{ strategy: "fixed" }}
                          renderOnMount
                          className="dropdown-with-icons"
                        >
                          <Dropdown.Item
                            as="div"
                            className="p-cursor"
                            onClick={() => {
                              setSelectedUOM(item);
                              setOpenEditModal(true);
                            }}
                          >
                            <EditIcon /> Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            as="div"
                            onClick={() => handleDeleteUOM(item)}
                            className="p-cursor"
                          >
                            <DeleteIcon color="#94A3B8" />
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                    <td>
                      {(queryParams.page - 1) * queryParams.limit + i + 1}
                    </td>
                    <td>{item.title}</td>
                    <td>{item.uom}</td>
                    <td>{item.baseUom}</td>
                    <td>{item.baseQty}</td>
                    <td>{format(new Date(item.createdAt), "dd/MM/yyyy")}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Show empty state when no data */}
        {!hasData && !isFetching && (
          <EmptyState
            hasFilters={hasActiveFilters}
            onCreateNew={() => setOpenCreateModal(true)}
            onClearFilters={clearAllFilters}
          />
        )}

        {/* Show legacy NoTableItem component only when there's data but empty results */}
        {hasData && data?.uom?.length < 1 && (
          <NoTableItem queryParams={queryParams} />
        )}
      </div>

      <ModalLoader show={isFetching || deleteUOMMutation.isLoading} />

      {openCreateModal && (
        <AddEditUOMModal
          show={openCreateModal}
          onHide={() => setOpenCreateModal(false)}
          validationSchema={uomValidationSchema}
          title="Create New UOM"
          refetchData={refetch}
        />
      )}

      {openEditModal && selectedUOM && (
        <AddEditUOMModal
          show={openEditModal}
          onHide={() => {
            setOpenEditModal(false);
            setSelectedUOM(null);
          }}
          validationSchema={uomValidationSchema}
          title="Edit UOM"
          initialValues={selectedUOM}
          refetchData={refetch}
        />
      )}
    </main>
  );
}

export default UOMManagement;

function AddEditUOMModal({
  show,
  onHide,
  validationSchema,
  title,
  initialValues = {
    title: "",
    uom: "",
    baseUom: "",
    baseQty: "",
  },
  refetchData,
}) {
  const { Units } = useUnits();
  const { backendUrl, user: authUser } = useAuth();
  const isEditMode = !!initialValues.id;

  // Create UOM mutation
  const createUOMMutation = useMutation(
    payload =>
      fetchActionsUtil(
        `${backendUrl}/api/uom`,
        "POST",
        authUser.token,
        payload
      ),
    {
      onSuccess: () => {
        toast.success("UOM created successfully");
        refetchData();
        onHide();
      },
      onError: ({ message }) => {
        toast.error(message || "Unable to create UOM: Try again");
      },
    }
  );

  // Update UOM mutation
  const updateUOMMutation = useMutation(
    ({ id, ...payload }) =>
      fetchActionsUtil(
        `${backendUrl}/api/uom/${id}`,
        "PUT",
        authUser.token,
        payload
      ),
    {
      onSuccess: () => {
        toast.success("UOM updated successfully");
        refetchData();
        onHide();
      },
      onError: ({ message }) => {
        toast.error(message || "Unable to update UOM: Try again");
      },
    }
  );

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    if (isEditMode) {
      updateUOMMutation.mutate(
        { id: initialValues.id, ...values },
        {
          onSuccess: () => {
            setSubmitting(false);
            resetForm();
          },
          onError: () => {
            setSubmitting(false);
          },
        }
      );
    } else {
      createUOMMutation.mutate(values, {
        onSuccess: () => {
          setSubmitting(false);
          resetForm();
        },
        onError: () => {
          setSubmitting(false);
        },
      });
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const isLoading = createUOMMutation.isLoading || updateUOMMutation.isLoading;

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title className="h5">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.title && formik.errors.title}
              placeholder="Enter title"
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.title}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>UOM *</Form.Label>
            <CreatableSelect
              options={Units}
              name="uom"
              placeholder="Select or create UOM"
              value={
                Units?.find(unit => unit.value === formik.values.uom) || {
                  label: formik.values.uom,
                  value: formik.values.uom,
                }
              }
              onChange={selectedOption => {
                formik.setFieldValue("uom", selectedOption?.value || "");
              }}
              onBlur={formik.handleBlur}
              isClearable
              formatCreateLabel={inputValue => `Create "${inputValue}"`}
              className={
                formik.touched.uom && formik.errors.uom ? "is-invalid" : ""
              }
              isSearchable
            />
            {formik.touched.uom && formik.errors.uom && (
              <div className="invalid-feedback d-block">
                {formik.errors.uom}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sub UOM *</Form.Label>
            <CreatableSelect
              name="baseUom"
              placeholder="Select or create Sub UOM"
              value={
                Units?.find(unit => unit.value === formik.values.baseUom) || {
                  label: formik.values.baseUom,
                  value: formik.values.baseUom,
                }
              }
              onChange={selectedOption => {
                formik.setFieldValue("baseUom", selectedOption?.value || "");
              }}
              onBlur={formik.handleBlur}
              options={Units}
              isClearable
              formatCreateLabel={inputValue => `Create "${inputValue}"`}
              className={
                formik.touched.baseUom && formik.errors.baseUom
                  ? "is-invalid"
                  : ""
              }
              isSearchable
            />
            {formik.touched.baseUom && formik.errors.baseUom && (
              <div className="invalid-feedback d-block">
                {formik.errors.baseUom}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Base Quantity *</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              name="baseQty"
              value={formik.values.baseQty}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.baseQty && formik.errors.baseQty}
              placeholder="Enter base quantity"
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.baseQty}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              variant="secondary"
              onClick={onHide}
              disabled={formik.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting || isLoading}
            >
              {formik.isSubmitting || isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {isEditMode ? "Updating..." : "Creating..."}
                </>
              ) : isEditMode ? (
                "Update UOM"
              ) : (
                "Create UOM"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

// UOM PICKER
export function UOMPicker({ value, onChange, isClearable = false }) {
  const { backendUrl, user: authUser } = useAuth();
  // Fetch UOM options from API
  const { data: uomData, isLoading } = useQuery(
    ["UOM-OPTIONS"],
    () => fetchActionsUtil(`${backendUrl}/api/uom`, "GET", authUser.token),
    {
      select: data => {
        // Transform the API data into options format for UOM management
        const uomOptions =
          data?.uoms?.map(uom => ({
            label: `${uom.uom} - ${uom.baseUom} (${uom.quantity})`,
            value: uom.id,
            uom: uom.uom || "",
            baseUom: uom?.baseUom || "",
            baseQty: uom.baseQty || 1,
            title: uom.title || "",
          })) || [];

        return uomOptions;
      },
    }
  );

  try {
    const selectedOption = uomData?.find(unit => unit.value === value) || null;

    const formatOptionLabel = ({
      uom = "",
      baseUom = "",
      baseQty,
      title,
      value,
    }) => (
      <div className="d-flex flex-column">
        <div className="text-dark">
          <span className="fw-5">{title || "..."}</span> | {uom} - {baseUom} |
          Qty: {baseQty}
        </div>
      </div>
    );

    const customStyles = {
      option: (provided, state) => ({
        ...provided,
        padding: "8px 12px",
        borderBottom: "1px solid #f0f0f0",
        "&:last-child": {
          borderBottom: "none",
        },
      }),
      singleValue: provided => ({
        ...provided,
        color: "#333",
      }),
    };

    return (
      <Select
        options={uomData || []}
        value={selectedOption}
        onChange={onChange}
        isClearable={isClearable}
        isSearchable
        isLoading={isLoading}
        placeholder="Select UOM..."
        formatOptionLabel={formatOptionLabel}
        styles={customStyles}
        filterOption={(option, inputValue) => {
          const { uom, baseUom = "", title } = option.data;
          const searchTerm = inputValue.toLowerCase();
          return (
            uom?.toLowerCase().includes(searchTerm) ||
            baseUom?.toLowerCase().includes(searchTerm) ||
            title?.toLowerCase().includes(searchTerm)
          );
        }}
      />
    );
  } catch (error) {
    console.error("Error in UOMPicker:", error);
    return null;
  }
}
